import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Subscription, combineLatest, map } from 'rxjs';
import { ApplicationUnreadService } from '../application-unread.service';
import { ChatUnreadService } from '../chat-unread.service';
import { BadgeState, PushPlatform } from './push-platform';

/**
 * Keeps the app icon badge (installed app / home screen) equal to the unread
 * chats plus the unread applications while the app is open. While it is
 * closed, push-sw.js updates the badge from incoming pushes, starting from the
 * state stored here.
 *
 * Every chat resync re-applies the badge, even with an unchanged count: the
 * service worker may have raised it while the app was in the background.
 */
@Injectable({ providedIn: 'root' })
export class AppBadgeService {
  private readonly platform = inject(PushPlatform);
  private readonly chatUnread = inject(ChatUnreadService);
  private readonly applicationUnread = inject(ApplicationUnreadService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly applicationCount$ = toObservable(
    this.applicationUnread.count
  );
  private started = false;
  private subscription?: Subscription;
  private queue: Promise<void> = Promise.resolve();

  start(): void {
    if (this.started) return;
    this.started = true;
    this.subscription = combineLatest([
      this.chatUnread.count$,
      this.applicationCount$,
    ])
      .pipe(
        map(([chat, applications]) => ({ chat, applications })),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(state => void this.apply(state));
  }

  /** Stops following the counts and clears badge and stored state, e.g. on logout. */
  async reset(): Promise<void> {
    this.subscription?.unsubscribe();
    this.started = false;
    await this.apply({ chat: 0, applications: 0 });
  }

  /** Runs one after the other, so a slower earlier write can't win. */
  private apply(state: BadgeState): Promise<void> {
    this.queue = this.queue.then(() => this.write(state));
    return this.queue;
  }

  private async write(state: BadgeState): Promise<void> {
    try {
      await this.platform.writeBadgeState(state);
      await this.platform.setBadge(state.chat + state.applications);
    } catch {
      // Badges are optional: unsupported, or not permitted yet on iOS.
    }
  }
}
